// The one place a number field's Zod type failure becomes an operator-facing message (BUG-2569, BUG-2571).
// Zod 4 reports a missing value and +/-Infinity as `invalid_type` ("expected number, received undefined").

/** What to say for each way a number field's type check can fail. */
export interface NumberFieldMessages {
  /** The field is empty (`undefined`): say what to enter, e.g. "Enter a rate." */
  missing: string;
  /** A pasted number too large for a JS number (parses to `+Infinity`). */
  tooLarge: string;
  /** A pasted negative number too large for a JS number (parses to `-Infinity`). */
  tooSmall: string;
}

/**
 * Build the `error` option for `z.number({ error })`, so a type failure reads as
 * the operator's own mistake instead of Zod's default wording.
 *
 * Zod 4 refuses `+/-Infinity` as `invalid_type` BEFORE `.finite()` runs, and its
 * default text reads "expected number, received number" (BUG-2569); a missing
 * value reads "expected number, received undefined" (BUG-2571). Any other
 * failure (a string, `NaN`) returns `undefined` and keeps Zod's text.
 *
 * @param messages - The copy for an empty field and for each overflow direction.
 * @returns A Zod error customizer.
 * @sideEffect None — pure.
 */
export function numberFieldError(messages: NumberFieldMessages) {
  return (issue: { input?: unknown }): string | undefined => {
    if (issue.input === undefined) return messages.missing;
    if (issue.input === Number.POSITIVE_INFINITY) return messages.tooLarge;
    if (issue.input === Number.NEGATIVE_INFINITY) return messages.tooSmall;
    return undefined;
  };
}

/** A percent or fraction rate field (commission rate, buyer's-premium rate), phrased in percent. */
export const RATE_FIELD_ERROR = numberFieldError({
  missing: 'Enter a rate.',
  tooLarge: 'Rate cannot be above 100%.',
  tooSmall: 'Rate cannot be negative.',
});

/** A money amount on a fee schedule (a tier boundary, a flat fee, a premium floor or cap). */
export const AMOUNT_FIELD_ERROR = numberFieldError({
  missing: 'Enter an amount.',
  tooLarge: 'Amount is too large.',
  tooSmall: 'Amount cannot be negative.',
});
